import React, { useState } from 'react';
import { PlusLg } from 'react-bootstrap-icons';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import { toast } from 'react-toastify';

import { FormElement } from '../../../components/FormElement';
import { LoadingSkeletonTableRows } from '../../../components/LoadingSkeletonTableRows';
import { Group } from '../../../domain/group';
import { Permission } from '../../../domain/permissions';
import { useCreateGroupRepository } from '../../../repository/groups/useCreateGroupRepository';
import { useGroupsRepository } from '../../../repository/groups/useGroupsRepository';
import { useModifyGroupRepository } from '../../../repository/groups/useModifyGroupRepository';
import { useRemoveGroupRepository } from '../../../repository/groups/useRemoveGroupRepository';
import { GroupModal } from './GroupModal';
import { GroupsTableRow } from './GroupsTableRow';

export const Groups = () => {
    const { data: allGroups, isLoading } = useGroupsRepository();
    const { createGroup } = useCreateGroupRepository();

    const { modifyGroup } = useModifyGroupRepository();
    const { removeGroup } = useRemoveGroupRepository();

    const [showModal, setShowModal] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState<Group | undefined>(undefined);

    const handleAddGroup = () => {
        setSelectedGroup(undefined);
        setShowModal(true);
    };

    const handleEditGroup = (group: Group) => {
        setSelectedGroup(group);
        setShowModal(true);
    };

    const handleDeleteGroup = async (id: string) => {
        if (window.confirm('Czy na pewno chcesz usunąć tę grupę?')) {
            try {
                await removeGroup(id);
                toast.success('Grupa została usunięta');
            } catch (e) {
                toast.error('Wystąpił błąd podczas usuwania grupy. Upewnij się, że masz odpowiednie uprawnienia.');
            }
        }
    };

    const handleSaveGroup = async (name: string, description: string, permissions: Permission[]) => {
        try {
            if (selectedGroup) {
                await modifyGroup({ id: selectedGroup.id, name, description, permissions });
                toast.success('Grupa została zaktualizowana');
            } else {
                await createGroup({ name, description, permissions });
                toast.success('Grupa została utworzona');
            }
        } catch (e) {
            toast.error('Wystąpił błąd podczas zapisywania grupy. Upewnij się, że masz odpowiednie uprawnienia.');
            throw e;
        }
    };

    return (
        <FormElement
            headingText={
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Grupy użytkowników</h4>
                    <Button onClick={handleAddGroup}>
                        <PlusLg /> Nowa grupa
                    </Button>
                </div>
            }
        >
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Nazwa</th>
                        <th>Opis</th>
                        <th>Uprawnienia</th>
                        <th className="text-end">Akcje</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <LoadingSkeletonTableRows rows={3} columns={4} />
                    ) : (
                        allGroups?.map((group) => (
                            <GroupsTableRow
                                key={group.id}
                                group={group}
                                onEdit={handleEditGroup}
                                onDelete={handleDeleteGroup}
                            />
                        ))
                    )}
                </tbody>
            </Table>
            <GroupModal
                show={showModal}
                group={selectedGroup}
                onClose={() => setShowModal(false)}
                onSave={handleSaveGroup}
            />
        </FormElement>
    );
};
